/**
 * Axios HTTP Client with Authentication Interceptors
 *
 * Architecture:
 * - Auth errors trigger immediate redirect (not error propagation)
 * - Uses window.location.href for synchronous redirect (stops React)
 * - Preserves original URL for redirect after login
 * - No coordination with AuthContext needed for auth failures
 * - All API errors are automatically reported to GlitchTip/Sentry
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import * as Sentry from '@sentry/nextjs';
import { clientCookies } from '@/lib/auth/cookies';

// ============================================================================
// TYPES
// ============================================================================

interface JwtPayload {
  exp: number;
  id: number;
  userType: string;
}

export interface RequestConfig extends InternalAxiosRequestConfig {
  skipAuth?: boolean;
}

// ============================================================================
// AUTH REDIRECT HELPER
// ============================================================================

/**
 * Handles auth failures by redirecting to login
 * Uses window.location.href for synchronous redirect that stops React
 */
function handleAuthFailure(): never {
  // Clear token
  clientCookies.removeToken();

  // Get current path for redirect after login
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const redirectParam =
    currentPath !== '/login'
      ? `?redirect=${encodeURIComponent(currentPath)}&session=expired`
      : '?session=expired';

  // Redirect immediately (synchronous, stops React rendering)
  if (typeof window !== 'undefined') {
    window.location.href = `/login${redirectParam}`;
  }

  // This throw ensures TypeScript knows we never continue
  throw new Error('Auth redirect');
}

// ============================================================================
// AXIOS INSTANCE CONFIGURATION
// ============================================================================

const baseURL = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3000';
const timeout = parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT || '180000', 10);

const axiosClient: AxiosInstance = axios.create({
  baseURL,
  timeout,
});

// ============================================================================
// REQUEST INTERCEPTOR - PROACTIVE TOKEN VALIDATION
// ============================================================================

axiosClient.interceptors.request.use(
  (config: RequestConfig) => {
    // Skip auth injection for public endpoints (login, 2FA)
    if (config.skipAuth) {
      return config;
    }

    const token = clientCookies.getToken();

    // No token - let it proceed, backend will return 401 if needed
    if (!token) {
      return config;
    }

    // Demo mode tokens start with 'demo-token-' and don't need JWT validation
    if (token.startsWith('demo-token-')) {
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    }

    try {
      // Decode JWT to check expiry BEFORE making request
      const decoded = jwtDecode<JwtPayload>(token);
      const now = Math.floor(Date.now() / 1000);

      // Token expired - redirect immediately
      if (decoded.exp <= now) {
        handleAuthFailure();
      }

      // Token valid - inject into Authorization header
      config.headers.Authorization = `Bearer ${token}`;

      return config;
    } catch {
      // Token decode failed - invalid format, redirect
      handleAuthFailure();
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============================================================================
// RESPONSE INTERCEPTOR - CENTRALIZED 401 HANDLING + SENTRY REPORTING
// ============================================================================

axiosClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    // Get the original request config
    const config = error.config as RequestConfig | undefined;

    // Handle 401 Unauthorized - but NOT for auth endpoints (login, 2FA)
    // Auth endpoints with skipAuth should pass the 401 to the caller to show proper error
    if (error.response?.status === 401 && !config?.skipAuth) {
      handleAuthFailure();
    }

    // Report API errors to GlitchTip/Sentry (except 401 auth errors which are expected)
    const status = error.response?.status;
    const shouldReport = status !== 401 && status !== 403 && status !== 404;

    if (shouldReport) {
      Sentry.withScope((scope) => {
        // Set error context
        scope.setTag('error_type', 'api_error');
        scope.setTag('http_status', String(status || 'network_error'));
        scope.setTag('http_method', config?.method?.toUpperCase() || 'UNKNOWN');

        // Add request details (redact sensitive parts)
        scope.setContext('request', {
          url: config?.url ? redactUrl(config.url) : 'unknown',
          method: config?.method,
          baseURL: config?.baseURL,
          timeout: config?.timeout,
        });

        // Add response details
        if (error.response) {
          const sanitized = sanitizeResponseData(error.response.data);
          scope.setContext('response', {
            status: error.response.status,
            statusText: error.response.statusText,
            // JSON stringify to preserve array contents in Sentry display
            data: typeof sanitized === 'object' ? JSON.stringify(sanitized) : sanitized,
          });
        }

        // Capture the error with fingerprint for grouping similar errors
        Sentry.captureException(error, {
          fingerprint: ['api-error', config?.method || '', config?.url || '', String(status)],
        });
      });
    }

    // All other errors (including 401 from login) - pass through to component error handling
    return Promise.reject(error);
  }
);

// ============================================================================
// SECURITY HELPERS
// ============================================================================

/**
 * Redacts sensitive information from URLs before sending to Sentry
 */
function redactUrl(url: string): string {
  return url
    .replace(/token=[^&]+/gi, 'token=[REDACTED]')
    .replace(/apiKey=[^&]+/gi, 'apiKey=[REDACTED]')
    .replace(/password=[^&]+/gi, 'password=[REDACTED]');
}

/**
 * Sanitizes response data - redacts sensitive fields but keeps the rest for debugging
 */
function sanitizeResponseData(data: unknown): unknown {
  if (!data) return data;
  if (typeof data !== 'object') return data;

  const sensitiveKeys = [
    'password',
    'token',
    'secret',
    'cookie',
    'authorization',
    'apikey',
    'api_key',
  ];

  function redactObject(obj: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase();

      // Redact sensitive fields
      if (sensitiveKeys.some((k) => lowerKey.includes(k))) {
        result[key] = '[REDACTED]';
      } else if (value && typeof value === 'object' && !Array.isArray(value)) {
        // Recursively sanitize nested objects
        result[key] = redactObject(value as Record<string, unknown>);
      } else if (Array.isArray(value)) {
        // Sanitize arrays
        result[key] = value.map((item) =>
          item && typeof item === 'object' ? redactObject(item as Record<string, unknown>) : item
        );
      } else {
        result[key] = value;
      }
    }

    return result;
  }

  return redactObject(data as Record<string, unknown>);
}

// ============================================================================
// EXPORTS
// ============================================================================

export default axiosClient;
export { axiosClient };
